import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { EventModal } from '../components/EventModal';
import type { CalendarEventSummary, GoogleConnectionStatus } from '../types';

type ListResponse = { items: CalendarEventSummary[] };

const EVENT_COLORS = ['ev-blue', 'ev-coral', 'ev-teal', 'ev-amber', 'ev-purple'] as const;
function colorForEvent(id: string): (typeof EVENT_COLORS)[number] {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return EVENT_COLORS[Math.abs(h) % EVENT_COLORS.length]!;
}

export function Calendar() {
  const { t } = useTranslation(['calendar', 'common', 'google']);
  const [view, setView] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() }; // month 0-indexed
  });
  const [events, setEvents] = useState<CalendarEventSummary[]>([]);
  const [conn, setConn] = useState<GoogleConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Modal state: when editing, holds the event; when creating, holds the
  // (optional) date to seed the form. Both null = modal closed.
  const [modal, setModal] = useState<
    { mode: 'edit'; event: CalendarEventSummary } | { mode: 'new'; date?: Date } | null
  >(null);

  const reloadEvents = useCallback(async () => {
    const monthStart = new Date(view.year, view.month, 1);
    const monthEnd = new Date(view.year, view.month + 1, 0, 23, 59, 59);
    const params = new URLSearchParams({
      start: monthStart.toISOString(),
      end: monthEnd.toISOString(),
    });
    setLoading(true);
    try {
      const d = await apiFetch<ListResponse>(`/api/calendar/events?${params}`);
      setEvents(d.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [view.year, view.month]);

  // Fetch events whenever the visible month changes
  useEffect(() => {
    void reloadEvents();
  }, [reloadEvents]);

  useEffect(() => {
    apiFetch<GoogleConnectionStatus>('/api/auth/google/status')
      .then(setConn)
      .catch(() => setConn(null));
  }, []);

  const monthGrid = useMemo(() => {
    const firstOfMonth = new Date(view.year, view.month, 1);
    const lastOfMonth = new Date(view.year, view.month + 1, 0);
    // Sunday-start grid (Israeli convention; matches mockup which lists א'-ש')
    const startWeekday = firstOfMonth.getDay(); // 0=Sun
    const daysInMonth = lastOfMonth.getDate();
    const cells: { date: Date; inMonth: boolean }[] = [];
    // Leading blanks from the previous month
    for (let i = 0; i < startWeekday; i++) {
      const d = new Date(view.year, view.month, -i);
      cells.unshift({ date: d, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(view.year, view.month, d), inMonth: true });
    }
    // Trailing blanks to fill the last row to 7
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1]!;
      cells.push({
        date: new Date(last.date.getFullYear(), last.date.getMonth(), last.date.getDate() + 1),
        inMonth: false,
      });
    }
    return cells;
  }, [view.year, view.month]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEventSummary[]>();
    for (const ev of events) {
      const d = new Date(ev.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const list = map.get(key) ?? [];
      list.push(ev);
      map.set(key, list);
    }
    return map;
  }, [events]);

  const todayKey = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}`;
  }, []);

  const monthName = t(`months.${view.month + 1}`);

  async function changeMonth(delta: number) {
    setView((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  }

  async function syncNow() {
    setSyncing(true);
    setError(null);
    try {
      const res = await apiFetch<{ upserted: number; deleted: number }>('/api/calendar/sync', {
        method: 'POST',
      });
      // Re-fetch events for the visible month
      const monthStart = new Date(view.year, view.month, 1);
      const monthEnd = new Date(view.year, view.month + 1, 0, 23, 59, 59);
      const params = new URLSearchParams({
        start: monthStart.toISOString(),
        end: monthEnd.toISOString(),
      });
      const fresh = await apiFetch<ListResponse>(`/api/calendar/events?${params}`);
      setEvents(fresh.items);
      alert(t('google.synced_just_now', { upserted: res.upserted }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div>
      <div className="cal-nav">
        <div className="cal-nav-month">
          <button className="cal-nav-btn" onClick={() => changeMonth(-1)}>
            {t('prev_month')}
          </button>
          <div className="cal-nav-title">
            {t('month_year', { month: monthName, year: view.year })}
          </div>
          <button className="cal-nav-btn" onClick={() => changeMonth(1)}>
            {t('next_month')}
          </button>
        </div>
        <div className="cal-nav-actions">
          <button
            className="cal-nav-btn cal-nav-btn-primary"
            onClick={() => setModal({ mode: 'new' })}
          >
            {t('new_event')}
          </button>
          {conn?.connected ? (
            <button className="cal-nav-btn" onClick={syncNow} disabled={syncing}>
              {syncing ? t('google.syncing') : t('google.sync_now')}
            </button>
          ) : (
            <span className="cal-nav-disconnected" title={t('google.not_connected')}>
              {t('google.not_connected')}
            </span>
          )}
        </div>
      </div>

      {error && <div className="signin-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="cal-grid-wrap">
        <div className="cal-weekdays">
          {(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const).map((d) => (
            <div key={d} className="cal-wd">
              {t(`weekdays.${d}`)}
            </div>
          ))}
        </div>
        <div className="cal-days">
          {monthGrid.map((cell, i) => {
            const key = `${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}`;
            const dayEvents = eventsByDay.get(key) ?? [];
            const weekend = cell.date.getDay() === 6 || cell.date.getDay() === 5;
            const today = key === todayKey;
            return (
              <div
                key={i}
                className={`cal-day ${cell.inMonth ? '' : 'other-month'} ${weekend ? 'weekend' : ''} ${today ? 'today' : ''}`}
                onClick={() => setModal({ mode: 'new', date: new Date(cell.date) })}
              >
                <div className="day-num">{cell.date.getDate()}</div>
                {dayEvents.slice(0, 3).map((ev) => (
                  <div
                    key={ev.id}
                    className={`day-event ${colorForEvent(ev.id)}`}
                    title={ev.title}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModal({ mode: 'edit', event: ev });
                    }}
                  >
                    {ev.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="day-event-more">+{dayEvents.length - 3}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {loading && events.length === 0 && (
        <div className="empty" style={{ marginTop: 12 }}>
          {t('loading')}
        </div>
      )}

      {modal && (
        <EventModal
          {...(modal.mode === 'edit'
            ? { initial: modal.event }
            : modal.date
              ? { defaultDate: modal.date }
              : {})}
          onSaved={() => {
            setModal(null);
            void reloadEvents();
          }}
          onClose={() => setModal(null)}
          onDeleted={() => {
            setModal(null);
            void reloadEvents();
          }}
        />
      )}
    </div>
  );
}
